import { forwardRef, MouseEventHandler, ReactElement } from 'react'
import { IconButton as MuiIconButton, styled, Tooltip } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { IconContext } from '@phosphor-icons/react'

export type IconButtonProps = {
    icon: ReactElement
    size?: 'small' | 'medium'
    selected: boolean
    onChange: MouseEventHandler<HTMLButtonElement>
    ariaLabel?: string
    disabled?: boolean
    tooltip?: string
}

export const BooleanButton = forwardRef<HTMLButtonElement, IconButtonProps>(
    ({ icon, onChange, ariaLabel, disabled, selected, tooltip, size = 'small', ...rest }, ref) => {
        return (
            <Tooltip title={tooltip} placement="top">
                <span>
                    <StyledBooleanButton
                        size={size}
                        $selected={selected}
                        onClick={onChange}
                        disableRipple
                        aria-label={ariaLabel}
                        disabled={disabled}
                        ref={ref}
                        {...rest}
                    >
                        <IconContext.Provider
                            value={{
                                size: size === 'small' ? tokens.SizingIconS : tokens.SizingIconM,
                                weight: size === 'small' ? 'bold' : undefined,
                            }}
                        >
                            {icon}
                        </IconContext.Provider>
                    </StyledBooleanButton>
                </span>
            </Tooltip>
        )
    }
)

const StyledBooleanButton = styled(MuiIconButton, {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $selected: boolean }>(({ $selected }: { $selected: boolean }) => ({
    padding: tokens.SizingSpacingNone,
    borderRadius: tokens.SizingCornerS,
    ...($selected
        ? {
              backgroundColor: tokens.ColorSurfaceSelectedDefault,
              boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} ${tokens.ColorBorderSelectedDefault}`,
              color: tokens.ColorIconLinkSelected,
              '&.Mui-disabled': {
                  backgroundColor: tokens.ColorSurfaceDisabled,
                  color: tokens.ColorIconDisabled,
                  //TODO: replace ColorIconDisabled with color/border/primary/inactive/selected when it's available
                  boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} ${tokens.ColorIconDisabled}`,
              },
          }
        : {
              color: tokens.ColorIconBoldest,
              '&.Mui-disabled': {
                  color: tokens.ColorIconDisabled,
                  boxShadow: 'none',
              },
          }),
    '&:hover': $selected
        ? {
              backgroundColor: tokens.ColorSurfaceHover,
              boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} ${tokens.ColorBorderSelectedDefault}`,
              color: tokens.ColorIconLinkSelected,
          }
        : {
              backgroundColor: tokens.ColorSurfaceHover,
              color: tokens.ColorIconBoldest,
          },
    '&:active': $selected
        ? {
              backgroundColor: tokens.ColorSurfacePressed,
              boxShadow: `inset 0 0 0 ${tokens.SizingBorderS} ${tokens.ColorBorderSelectedDefault}`,
              color: tokens.ColorIconLinkSelected,
          }
        : {
              backgroundColor: tokens.ColorSurfacePressed,
              color: tokens.ColorIconBoldest,
          },
    '&.MuiIconButton-sizeSmall': {
        width: '32px',
        height: '32px',
    },
    '&.MuiIconButton-sizeMedium': {
        width: '40px',
        height: '40px',
    },
}))
